import { useState, useEffect } from 'react';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import { UserButton } from '@clerk/clerk-react';
import { useAuth } from '../hooks/useAuth.js';
import Sidebar from './Sidebar.jsx';
import ContextPill from './shared/ContextPill.jsx';
import HelpPanel from './shared/HelpPanel.jsx';
import { useHelpContext } from './shared/HelpContext.jsx';
import { Drawer } from './primitives/Drawer.jsx';
import { ROUTES, NAV_GROUPS, isRouteFeatureEnabled } from '../routes.js';
import { useSearch } from '../context/SearchContext.jsx';
import UnifiedSearch from './shared/UnifiedSearch.jsx';

const BYPASS = import.meta.env.VITE_BYPASS_AUTH === 'true';
const SIDEBAR_LS = 'sitruna_hub_sidebar_collapsed';

// Nav items grouped for mobile drawer — visible routes only (navVisible !== false)
const NAV_BY_GROUP = NAV_GROUPS.map((g) => ({
  ...g,
  routes: ROUTES.filter(
    (r) => r.group === g.id && r.navVisible !== false && !r.desktopOnly && isRouteFeatureEnabled(r)
  ),
})).filter((g) => g.routes.length > 0);

function getBreadcrumb(pathname, params) {
  // Match exact path first, then prefix
  const route = ROUTES.find((r) => {
    // Strip :param segments to compare prefix
    const prefix = r.path.split('/:')[0];
    return pathname === r.path || pathname.startsWith(prefix + '/');
  });
  if (!route) return '';
  const crumbs = typeof route.breadcrumbs === 'function'
    ? route.breadcrumbs(params)
    : route.breadcrumbs;
  return crumbs[crumbs.length - 1] || '';
}

function loadSidebarCollapsed() {
  try {
    return localStorage.getItem(SIDEBAR_LS) === 'true';
  } catch {
    return false;
  }
}

export default function Layout({ children }) {
  const { role, email } = useAuth();
  const location = useLocation();
  const params = useParams();
  const { isOpen: helpOpen, content: helpContent, close: closeHelp, open: openHelp } = useHelpContext();
  const { open: openSearch } = useSearch();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(loadSidebarCollapsed);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function handleKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        openSearch();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [openSearch]);

  function toggleSidebar() {
    setCollapsed((v) => {
      const next = !v;
      try {
        localStorage.setItem(SIDEBAR_LS, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }

  const crumb = getBreadcrumb(location.pathname, params);

  return (
    <div className="h-screen flex overflow-hidden bg-n-25">
      <Sidebar collapsed={collapsed} onToggle={toggleSidebar} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar — slim, page chrome only */}
        <header className="h-[52px] md:h-11 shrink-0 bg-white border-b border-n-100 flex items-center gap-3 px-4 md:px-5 relative z-20">
          {/* Mobile hamburger */}
          <button
            type="button"
            aria-label="Toggle navigation"
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen((v) => !v)}
            className="md:hidden text-n-600 hover:text-n-800 p-1 -ml-1 rounded"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {mobileNavOpen ? (
                <>
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </>
              ) : (
                <>
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </>
              )}
            </svg>
          </button>

          {/* Mobile-only logo */}
          <NavLink to="/clients" className="md:hidden flex items-center gap-2 text-sitruna-black" aria-label="Sitruna Hub">
            <span className="w-[22px] h-[22px] grid place-items-center rounded bg-sitruna-yellow text-sitruna-black font-bold text-xs">S</span>
            <span className="text-[14px] font-semibold tracking-tight">Sitruna Hub</span>
          </NavLink>

          {/* Page title */}
          {crumb && (
            <span className="hidden md:inline text-sm font-semibold text-n-800">{crumb}</span>
          )}

          <div className="flex-1" />

          {/* Context pill — client/marketplace selection for scoped routes */}
          <div className="hidden md:flex">
            <ContextPill />
          </div>

          {/* Bell + Help — desktop only (user is in the sidebar) */}
          <div className="hidden md:flex items-center gap-1 ml-1">
            <button
              type="button"
              aria-label="Notifications"
              className="w-7 h-7 grid place-items-center rounded text-n-400 hover:text-n-700 hover:bg-n-50 transition-colors"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M6 16V11a6 6 0 0112 0v5l1.5 2H4.5z" />
                <path d="M10 20a2 2 0 004 0" />
              </svg>
            </button>
            <button
              type="button"
              aria-label="Help"
              onClick={openHelp}
              className="w-7 h-7 grid place-items-center rounded text-n-400 hover:text-n-700 hover:bg-n-50 transition-colors"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M9.5 9.5a2.5 2.5 0 015 .5c0 2-2.5 3-2.5 3" />
                <circle cx="12" cy="17" r=".5" fill="currentColor" />
              </svg>
            </button>
          </div>

          {/* Context pill — mobile (client-only) */}
          <div className="md:hidden">
            <ContextPill />
          </div>

          {/* Role badge + user (mobile only — sidebar has it on desktop) */}
          <div className="md:hidden flex items-center gap-2">
            <span className="text-n-500 text-xs capitalize">{role.replace('_', ' ')}</span>
            {BYPASS ? (
              <div
                className="w-7 h-7 rounded-full bg-sitruna-yellow flex items-center justify-center text-sitruna-black text-xs font-bold"
                title={email || 'Demo user'}
              >
                {(email || 'D')[0].toUpperCase()}
              </div>
            ) : (
              <UserButton afterSignOutUrl="/" />
            )}
          </div>
        </header>

        {/* Mobile slide-out nav — grouped from routes.js */}
        <Drawer open={mobileNavOpen} onOpenChange={setMobileNavOpen} title="Navigation">
          <nav className="flex flex-col -mx-5 -mt-4">
            {NAV_BY_GROUP.map((group) => (
              <div key={group.id}>
                <div className="px-5 pt-3 pb-1 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-n-400">
                  {group.label}
                </div>
                {group.routes.map(({ path, label }) => (
                  <NavLink
                    key={path}
                    to={path}
                    onClick={() => setMobileNavOpen(false)}
                    className={({ isActive }) =>
                      `px-5 py-2.5 text-sm font-medium border-l-2 ${
                        isActive
                          ? 'border-sitruna-yellow bg-sitruna-yellow/10 text-sitruna-black'
                          : 'border-transparent text-n-600 hover:text-n-800 hover:bg-n-50'
                      }`
                    }
                  >
                    {label}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>
        </Drawer>

        {/* Page content */}
        <main className="flex-1 flex flex-col overflow-hidden">{children}</main>
      </div>

      {/* Help panel — portalled over full layout */}
      {helpOpen && (
        <HelpPanel title="Help" onClose={closeHelp}>
          {helpContent ?? (
            <p className="text-n-400 text-sm">Help coming soon for this page.</p>
          )}
        </HelpPanel>
      )}

      {/* Unified search — portalled over full layout */}
      <UnifiedSearch />
    </div>
  );
}
